<template>
    <div class="space-y-1 py-2 text-xs text-white">
        <p v-if="!events.length" class="text-gray-300">No events connected to this object.</p>
        <button v-for="item in events" :key="item.node.id" type="button"
            class="flex w-full items-center justify-between gap-2 rounded border border-white/20 bg-white/5 px-1.5 py-0.5 text-left hover:border-ui-active"
            title="Select this event node" @click="selectEvent(item.node.id)">
            <span class="truncate">⚡ {{ item.node.data.event ? eventLabel(item.node.data.event) : '(no event selected)' }}</span>
            <span class="shrink-0 text-gray-300">{{ item.actions }} action{{ item.actions === 1 ? '' : 's' }}</span>
        </button>
        <div class="flex gap-1 pt-1">
            <form-button color="theme" width="auto" height="26px"
                title="Add an event node wired to this object" @click="addEvent">Add event</form-button>
            <form-button color="theme" width="auto" height="26px"
                title="Show the event and action nodes on the canvas" @click="logic.setVisible(true)">Show logic</form-button>
        </div>
    </div>
</template>

<script setup>
import { computed, toRaw } from 'vue';
import { useLogicStore } from '../../stores/logicStore.js';
import { useUndoStore } from '../../history/undoStore.js';
import { getEventActionWires, getEventNodesForObject } from '../../logic/logicGraph.js';
import { getObjectPage } from '../../utils/hasputils.js';
import { eventLabel } from '../../logic/logicPresentation.js';

/**
 * Replaces the former per-object Actions editor: actions are event/action
 * nodes now, so the properties panel only summarizes and links to them.
 */
const props = defineProps({
    node: { type: Object, required: true },
});
const logic = useLogicStore();
const undo = useUndoStore();

const page = computed(() => toRaw(getObjectPage(props.node)) ?? null);
const events = computed(() => {
    // page.logic is plain data outside Vue; these counters say when it changed.
    logic.revision;
    undo.historyVersion;
    const graph = page.value?.logic;
    if (!graph) return [];
    return getEventNodesForObject(graph, toRaw(props.node).id())
        .map(node => ({ node, actions: getEventActionWires(graph, node.id).length }));
});

function selectEvent(id) {
    logic.setVisible(true);
    logic.select(page.value, 'node', id);
}

function addEvent() {
    const entry = logic.palette.flatMap(group => group.entries).find(item => item.id === 'event');
    const layer = toRaw(props.node).getStage?.()?.logicLayer;
    const { error } = logic.addPaletteNode(page.value, entry, {
        anchorObject: { uuid: toRaw(props.node).id(), rect: layer.objectPageRect(props.node) },
        plateWidth: toRaw(props.node).getLayer?.()?.pageWidth ?? 0,
    });
    if (error) console.warn(error);
}
</script>
